import { IonButton, IonIcon, IonModal } from '@ionic/react'
import { addOutline, shareOutline } from 'ionicons/icons'

import type { InstallState } from '../lib/install'

/*
 * The ask: put this on your home screen.
 *
 * A bottom sheet rather than a banner, because Ionic already has the platform's
 * own sheet — the drag handle, the rubber-banding, the backdrop — and a banner
 * pinned above the dock would compete with the one thing on this screen the
 * customer is meant to be doing.
 *
 * Two entirely different contents, because the two platforms are two different
 * problems:
 *
 *   'prompt'  a button. Chrome's own install dialog opens from the event we
 *             captured at load, and there is nothing to explain.
 *   'ios'     instructions, because Safari has no install API at all and the
 *             customer has to do it by hand. A button here would be a lie.
 */
export function InstallSheet({
  state,
  businessName,
}: {
  state: InstallState
  businessName?: string
}) {
  const name = businessName || 'this app'

  return (
    <IonModal
      className="blooq-sheet"
      isOpen={state.sheetOpen && state.mode !== 'hidden'}
      onDidDismiss={state.closeSheet}
      /*
       * Sized to its own content, anchored to the bottom — see `.blooq-sheet`
       * in theme/blooq.css for the two custom properties that do it.
       *
       * Deliberately NOT Ionic's `breakpoints` sheet, which needs its height
       * declared as a fraction of the screen up front. The two states of this
       * sheet are a button and a three-step list, so any fraction is wrong for
       * one of them: a gap under the button, or instructions cut off.
       *
       * The customer dismissing it is the expected outcome rather than a
       * failure — see the note in lib/install.ts on why that means "not now".
       */
    >
      <div className="blooq-sheet__in">
        <span className="blooq-sheet__grab" aria-hidden="true" />
        <h2 className="blooq-sheet__title">Keep {name} on your phone</h2>

        {state.mode === 'ios' ? (
          <>
            <p className="blooq-sheet__lead">
              It opens like an app, full screen, with no address bar.
            </p>
            <ol className="blooq-sheet__steps">
              <li>
                <span>
                  Tap <IonIcon icon={shareOutline} aria-hidden="true" /> Share, at the bottom of
                  Safari.
                </span>
              </li>
              <li>
                <span>
                  Scroll down and tap <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li>
                <span>
                  Tap <strong>Add</strong>.
                </span>
              </li>
            </ol>
            <IonButton expand="block" fill="clear" onClick={state.closeSheet}>
              Got it
            </IonButton>
          </>
        ) : (
          <>
            <p className="blooq-sheet__lead">
              Adds an icon to your home screen. It opens full screen, and takes no
              more space than a bookmark.
            </p>
            <IonButton expand="block" onClick={state.install}>
              <IonIcon slot="start" icon={addOutline} />
              Add to home screen
            </IonButton>
            <IonButton expand="block" fill="clear" onClick={state.closeSheet}>
              Not now
            </IonButton>
          </>
        )}
      </div>
    </IonModal>
  )
}
